import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardHeader, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    toast({
      title: "Message sent!",
      description: "We'll get back to you within 24 hours.",
    });
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <section id="contact" className="py-24 bg-black">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
              Let's <span className="text-amber-400">Connect</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Got questions? Want to place a custom order? Or just want to say what's good?
              Hit us up.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">Get in Touch</h3>
                <p className="text-gray-300 mb-8">
                  We're here for partnerships, bulk orders, event catering, or just to talk
                  juice and hustle. Real people, real responses.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="bg-zinc-900 border-zinc-800 hover:border-amber-400/50 transition-colors">
                  <CardContent className="flex items-center space-x-4 pt-6">
                    <div className="bg-amber-400/10 p-3 rounded-lg">
                      <Mail className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Email Us</p>
                      <p className="text-white font-semibold">hello@teeazenshotz.com</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900 border-zinc-800 hover:border-amber-400/50 transition-colors">
                  <CardContent className="flex items-center space-x-4 pt-6">
                    <div className="bg-lime-400/10 p-3 rounded-lg">
                      <Phone className="w-6 h-6 text-lime-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Call or Text</p>
                      <p className="text-white font-semibold">775-383-9345</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900 border-zinc-800 hover:border-amber-400/50 transition-colors">
                  <CardContent className="flex items-center space-x-4 pt-6">
                    <div className="bg-orange-400/10 p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Find Us</p>
                      <p className="text-white font-semibold text-sm leading-relaxed">
                        255 S Orange Avenue<br />
                        Suite 104 #1741<br />
                        Orlando, FL 32801<br />
                        United States
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="pt-6">
                <p className="text-gray-400 text-sm">
                  <span className="text-white font-semibold">Hours:</span> Mon-Fri 9am-6pm EST
                  <br />
                  We usually respond within a few hours during business hours.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <h3 className="text-2xl font-bold text-white">Send a Message</h3>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-amber-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-amber-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-white">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(optional)"
                      className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-amber-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us what you need..."
                      rows={5}
                      className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-amber-400 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-amber-400 hover:bg-amber-500 text-black font-bold text-lg py-6"
                  >
                    Send Message
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};